import { useState, useRef, useCallback, useEffect } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@satyrsmc/shared/components";
import { TableCell, TableRow } from "@/components/ui/table";
import { Trash2, Check } from "lucide-react";
import type { LineItem } from "@satyrsmc/shared/client";

interface LineItemRowProps {
  item: LineItem;
  categories: string[];
  onUpdate: (id: string, updates: Partial<LineItem>) => Promise<void>;
  onDelete: (id: string) => void;
  onAddCategory: (name: string) => void;
}

/**
 * Local draft state for an editable field that saves to an async store.
 *
 * The input value is owned by the component so keystrokes appear instantly and are never
 * clobbered by a slow save or by a refetch resolving with stale data mid-typing. External
 * prop changes are only adopted while the user is not actively editing the field. Saves are
 * debounced and flushed on blur.
 */
function useDraftField(external: string, onCommit: (value: string) => void, delay = 400) {
  const [draft, setDraft] = useState(external);
  const editingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // Adopt external changes only when the user isn't actively editing this field.
  useEffect(() => {
    if (!editingRef.current) setDraft(external);
  }, [external]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handleChange = useCallback(
    (value: string) => {
      // Typing implies the user is editing — guard against external (refetch) overwrites
      // even if a focus event was never delivered.
      editingRef.current = true;
      setDraft(value);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        commitRef.current(value);
      }, delay);
    },
    [delay],
  );

  const handleFocus = useCallback(() => {
    editingRef.current = true;
  }, []);

  const handleBlur = useCallback(() => {
    editingRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
      commitRef.current(draft);
    }
  }, [draft]);

  return { draft, handleChange, handleFocus, handleBlur };
}

export function LineItemRow({
  item,
  categories,
  onUpdate,
  onDelete,
  onAddCategory,
}: LineItemRowProps) {
  const total = item.unitCost * item.quantity;
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showSaved = useCallback(() => {
    setSavedAt(Date.now());
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      setSavedAt(null);
      saveTimeoutRef.current = null;
    }, 2000);
  }, []);

  const handleUpdate = useCallback(
    async (updates: Partial<LineItem>) => {
      await onUpdate(item.id, updates);
      showSaved();
    },
    [onUpdate, item.id, showSaved],
  );

  const nameField = useDraftField(item.name, (v) => void handleUpdate({ name: v }));
  const commentsField = useDraftField(
    item.comments ?? "",
    (v) => void handleUpdate({ comments: v }),
  );
  const unitCostField = useDraftField(String(item.unitCost), (v) => {
    const n = parseFloat(v);
    if (!Number.isNaN(n) && n >= 0) void handleUpdate({ unitCost: n });
  });
  const quantityField = useDraftField(String(item.quantity), (v) => {
    const n = parseFloat(v);
    if (!Number.isNaN(n) && n >= 0) void handleUpdate({ quantity: n });
  });

  return (
    <TableRow>
      <TableCell className="max-sm:py-3">
        <Input
          value={nameField.draft}
          onChange={(e) => nameField.handleChange(e.target.value)}
          onFocus={nameField.handleFocus}
          onBlur={nameField.handleBlur}
          placeholder="Item name"
          className="h-8 text-sm"
        />
      </TableCell>
      <TableCell className="w-24 max-sm:py-3">
        <Input
          type="number"
          value={unitCostField.draft}
          onChange={(e) => unitCostField.handleChange(e.target.value)}
          onFocus={unitCostField.handleFocus}
          onBlur={unitCostField.handleBlur}
          min={0}
          step={0.01}
          className="h-8 text-sm"
        />
      </TableCell>
      <TableCell className="w-20 max-sm:py-3">
        <Input
          type="number"
          value={quantityField.draft}
          onChange={(e) => quantityField.handleChange(e.target.value)}
          onFocus={quantityField.handleFocus}
          onBlur={quantityField.handleBlur}
          min={0}
          step={1}
          className="h-8 text-sm"
        />
      </TableCell>
      <TableCell className="text-right font-medium tabular-nums max-sm:py-3">
        ${total.toLocaleString("en-US", { minimumFractionDigits: 2 })}
      </TableCell>
      <TableCell className="min-w-[120px] max-sm:py-3">
        <Select
          value={categories.includes(item.category) ? item.category : categories[0]}
          onValueChange={(v) => {
            if (v === "__add__") {
              const name = window.prompt("New category name:");
              if (name?.trim()) {
                onAddCategory(name.trim());
                void handleUpdate({ category: name.trim() });
              }
            } else {
              void handleUpdate({ category: v });
            }
          }}
        >
          <SelectTrigger className="h-8 text-sm" size="sm">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
            <SelectItem value="__add__">+ Add category</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="max-w-[160px] max-sm:py-3">
        <Input
          value={commentsField.draft}
          onChange={(e) => commentsField.handleChange(e.target.value)}
          onFocus={commentsField.handleFocus}
          onBlur={commentsField.handleBlur}
          placeholder="Comments"
          className="h-8 text-sm"
        />
      </TableCell>
      <TableCell className="min-w-[72px] max-sm:py-3">
        <div className="flex items-center gap-2">
          {savedAt != null && (
            <span className="flex items-center gap-1 text-xs text-green-600 animate-in fade-in duration-200">
              <Check className="size-3.5" />
              Saved
            </span>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => onDelete(item.id)}
            aria-label="Delete"
          >
            <Trash2 className="size-4 text-destructive" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}
